import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import {
  startOfDay, isToday, endOfDay, isAfter,
} from 'date-fns';
import BloodPressure from 'styles/blocks/bloodPressure';
import { STRINGS } from 'config';
import { VALIDATION_MESSAGES } from 'common/validationMessages';
import { restrictToNumberOnly } from 'common/inputs';
import media from 'styles/media';
import 'react-datepicker/dist/react-datepicker.css';
import { LabelledInput } from './labelledInput';

export const BloodPressureFields = ({
  reading, onUpdateReading, id, onRemoveReading, invalidFieldRef, setInvalidFieldRef, isFocussed,
}) => {
  const dateRef = useRef(() => React.createRef());
  const timeRef = useRef(() => React.createRef());
  const systolicRef = useRef(() => React.createRef());
  useEffect(() => {
    if (isFocussed && dateRef.current) {
      dateRef.current.input.focus();
    }
  }, [isFocussed]);
  const {
    systolic, diastolic, date, time, removable,
  } = reading;
  const alertHigh = (systolic && systolic > STRINGS.BIOMETRICS.SYSTOLIC.HIGH) || (diastolic && diastolic > STRINGS.BIOMETRICS.DIASTOLIC.HIGH);
  const alertLow = (systolic && systolic < STRINGS.BIOMETRICS.SYSTOLIC.LOW) || (diastolic && diastolic < STRINGS.BIOMETRICS.DIASTOLIC.LOW);
  const alertReversed = systolic && diastolic && (parseInt(systolic, 10) < parseInt(diastolic, 10));
  const [isBPInRange, setIsBPInRange] = useState({ alertHigh, alertLow, alertReversed });
  const [maxTime, setMaxTime] = useState(() => (date && !isToday(new Date(date)) ? endOfDay(new Date(date)) : new Date()));
  const validateBPRange = () => {
    if (systolic !== '' && diastolic !== '') {
      setIsBPInRange({ alertHigh, alertLow, alertReversed });
    }
  };
  const hideSoftKeyboard = event => {
    if (media.mobile) {
      event.target.blur();
    }
    if (invalidFieldRef && invalidFieldRef.current) {
      invalidFieldRef.current.scrollIntoView();
    }
  };
  const alertHighRef = useRef();
  const alertLowRef = useRef();
  const alertReversedRef = useRef();
  useEffect(() => {
    if (isBPInRange.alertHigh && alertHighRef.current) {
      setInvalidFieldRef(alertHighRef);
    } else if (isBPInRange.alertLow && alertLowRef.current) {
      setInvalidFieldRef(alertLowRef);
    } else if (isBPInRange.alertReversed && alertReversedRef.current) {
      setInvalidFieldRef(alertReversedRef);
    }
  }, [isBPInRange.alertHigh, isBPInRange.alertLow, isBPInRange.alertReversed, setInvalidFieldRef]);

  useEffect(() => {
    if (invalidFieldRef && invalidFieldRef.current) {
      invalidFieldRef.current.scrollIntoView();
    }
  }, [invalidFieldRef]);
  const validateDateAndTime = () => {
    if (date && isToday(new Date(date)) && time && isAfter(new Date(time), new Date())) {
      onUpdateReading(id, 'time', startOfDay(new Date()));
    }
  };
  const handleDateChange = dateTime => {
    if (isToday(dateTime)) {
      setMaxTime(new Date());
      /* If the date has been changed to today, and the time is set to later than now, set the time to startOfDay */
      if (time && isAfter(new Date(time), new Date())) {
        onUpdateReading(id, 'time', startOfDay(new Date()));
      }
    } else {
      setMaxTime(endOfDay(dateTime));
    }
    onUpdateReading(id, 'date', dateTime);
  };
  const handleKeyDown = (event, pickerRef) => {
    // Focus pickers own input if Escape key pressed
    if (event.key === 'Escape') {
      pickerRef.current.setState(
        { preventFocus: true },
        () => {
          pickerRef.current.setOpen(false);
          setTimeout(() => {
            pickerRef.current.setFocus();
            setTimeout(() => pickerRef.current.setState({ preventFocus: false }));
          });
        },
      );
    }
    // Close picker if user back-tabbed from field
    if (event.key === 'Tab' && event.shiftKey) {
      pickerRef.current.setOpen(false, true);
    }
  };
  const handleDateInputKeyDown = event => {
    handleKeyDown(event, dateRef);
    // If user presses enter, after all react-datepicker event the calendar is closed focus time input.
    // react-datepicker should pick this up in the picker's onSelect event.
    if (event.key === 'Enter') {
      setTimeout(() => (dateRef.current.isCalendarOpen() ? null : timeRef.current.input.focus()));
    }
  };

  // The following timepicker functions could be replaced with onSelect, but that isn't yet supported by the react-datepicker.
  const handleTimePopperKeyDown = event => {
    // We don't want to capture user input here. Use handleTimeInputKeyDown for that.
    if (event.target.nodeName === 'INPUT') {
      return;
    }
    if (event.key === 'Enter') {
      document.activeElement.click();
      setTimeout(() => (timeRef.current.isCalendarOpen() ? null : systolicRef.current.focus()));
    }
  };
  // We're only interested in clicks on time popper which are <li> elements
  const handleTimePopperClick = event => (event.target.nodeName === 'LI' ? systolicRef.current.focus() : null);
  const handleTimeInputKeyDown = event => {
    handleKeyDown(event, timeRef);
    // If user presses enter while timepicker is open close picker. If picker is closed, wait for picker to open and focus selected time.
    if (event.key === 'Enter') {
      if (timeRef.current.isCalendarOpen()) {
        timeRef.current.setOpen(false);
        systolicRef.current.focus();
      } else {
        setTimeout(() => {
          const isCalendarOpen = timeRef.current.isCalendarOpen() && timeRef.current.calendar.componentNode;
          if (!isCalendarOpen) {
            return;
          }
          const selectedDay = timeRef.current.calendar.componentNode.querySelector('.react-datepicker__time-list-item--selected');
          if (selectedDay) {
            selectedDay.focus({ preventScroll: true });
          }
        });
      }
    }
  };
  const [popperPlacement] = useState(() => (media.tablet && id !== '0' ? 'top-start' : 'bottom-start'));
  const renderError = () => {
    if (isBPInRange.alertReversed) {
      return <BloodPressure.Error ref={alertReversedRef}>{VALIDATION_MESSAGES.BLOODPRESSURE.READING_REVERSED}</BloodPressure.Error>;
    }
    if (isBPInRange.alertHigh) {
      return <BloodPressure.Error ref={alertHighRef}>{VALIDATION_MESSAGES.BLOODPRESSURE.READING_HIGH}</BloodPressure.Error>;
    }
    if (isBPInRange.alertLow) {
      return <BloodPressure.Error ref={alertLowRef}>{VALIDATION_MESSAGES.BLOODPRESSURE.READING_LOW}</BloodPressure.Error>;
    }
    return null;
  };
  return (
    <BloodPressure.FieldsContainerOuter removable={removable}>
      {renderError()}
      {removable && <BloodPressure.RemoveButton onClick={() => onRemoveReading(id)} aria-label="Delete blood pressure reading" type="button">X</BloodPressure.RemoveButton>}
      <BloodPressure.FieldsContainer>
        <BloodPressure.FieldsContextWrapper>
          <LabelledInput label="Date of reading" helpText="(for example: 14/01/2020)">
            <DatePicker
              selected={date}
              onChange={dateTime => handleDateChange(dateTime)}
              onSelect={() => timeRef.current.input.focus()}
              dateFormat={['dd/MM/yyyy', 'd/M/yy', 'd/M']}
              maxDate={new Date()}
              customInput={<BloodPressure.Input aria-label="Date of reading" />}
              onFocus={hideSoftKeyboard}
              onBlur={validateDateAndTime}
              popperPlacement={popperPlacement}
              ref={dateRef}
              onKeyDown={handleDateInputKeyDown}
            />
          </LabelledInput>
          <LabelledInput label="Time" helpText="(for example: 13:45)" onKeyDown={handleTimePopperKeyDown} onClick={handleTimePopperClick}>
            <DatePicker
              selected={time}
              onChange={dateTime => onUpdateReading(id, 'time', dateTime)}
              onKeyDown={handleTimeInputKeyDown}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              timeFormat="HH:mm"
              dateFormat={['HH:mm', 'HHmm', 'HH.mm']}
              customInput={<BloodPressure.Input aria-label="Time" />}
              minTime={startOfDay(new Date())}
              maxTime={maxTime}
              onFocus={hideSoftKeyboard}
              onBlur={validateDateAndTime}
              ref={timeRef}
              popperPlacement={popperPlacement}
            />
          </LabelledInput>
        </BloodPressure.FieldsContextWrapper>
        <BloodPressure.FieldsContextWrapper>
          <LabelledInput
            ref={systolicRef}
            label="Systolic BP (higher)"
            value={systolic}
            onChange={event => onUpdateReading(id, 'systolic', restrictToNumberOnly(event.target.value))}
            onBlur={() => validateBPRange()}
          />
          <LabelledInput
            label="Diastolic BP (lower)"
            value={diastolic}
            onChange={event => onUpdateReading(id, 'diastolic', restrictToNumberOnly(event.target.value))}
            onBlur={() => validateBPRange()}
          />
        </BloodPressure.FieldsContextWrapper>
      </BloodPressure.FieldsContainer>
    </BloodPressure.FieldsContainerOuter>
  );
};
